<template>
    <div>
        <!-- Search + Filters -->
        <div v-if="!selectedDocument" class="document-search-list">
            <div class="filter-bar">
                <input
                    v-model="searchText"
                    type="text"
                    class="search-input"
                    :placeholder="$t('forms.view_document.fields.search.placeholder')"
                    @input="onSearch"
                />
                <common-icon name="search" size="small" @click="getDocuments(doc)"></common-icon>
            </div>
            <div class="tab-filter">
                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.type.placeholder')"
                    :hasAutofocus="true"
                    :options="typeOptions"
                    :value="recordTypeId"
                    @change="changeTypeId($event)"
                ></select-input>

                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.source.placeholder')"
                    :hasAutofocus="true"
                    :options="sourceOptions"
                    :value="sourceId"
                    @change="changeSourceId($event)"
                ></select-input>
            </div>
        </div>
        <common-buttons v-if="!selectedDocument" align="right" class="download-print-btns">
            <common-button
                :label="$t('common.images.download')"
                type="secondary"
                :size="'tiny'"
                :disabled="!hasSelectedDocuments"
                @click="downloadSelected"
            ></common-button>
            <common-button
                :label="$t('common.images.print')"
                type="secondary"
                :size="'tiny'"
                :disabled="!hasSelectedDocuments"
                @click="printSelected"
            ></common-button>
        </common-buttons>

        <!-- Document List -->
        <div v-if="!selectedDocument" class="document-list">
            <template v-if="isLoading">
                <div class="loading-spinner">
                    <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                    <loading-spin></loading-spin>
                </div>
            </template>
            <div v-else>
                <div class="card-header">
                    <h3 class="card-title">
                        {{ $t('modals.patient_profile.profile.detail_form.card_head', { count: totalCount }) }}
                    </h3>
                    <label class="select-all">
                        <span class="doc-title">Select All</span>
                        <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll($event)" />
                    </label>
                </div>
                <div v-for="doc in documents" :key="doc.id" class="document-card">
                    <div class="doc-icon">{{ doc.icon }}</div>
                    <div class="doc-info">
                        <div class="doc-title">{{ doc.title }}</div>
                        <div class="doc-meta">
                            <span>{{ doc.date }}</span>
                            ·
                            <span>{{ doc.provider }}</span>
                            ·
                            <span>{{ doc.size }}</span>
                        </div>
                    </div>

                    <div class="doc-actions">
                        <div class="doc-tag" @click="viewDocument(doc.id)">{{ doc.type }}</div>
                        <common-icon name="view" size="small" @click="viewDocument(doc.id)"></common-icon>
                        <common-icon
                            v-if="isDocumentOwner(doc)"
                            name="trash-bin"
                            size="small"
                            class="doc-delete-icon"
                            @click="deleteDocument(doc)"
                        ></common-icon>
                        <div class="doc-checkbox-wrap">
                            <input type="checkbox" class="input-checkbox" :checked="doc.selected" @change="toggleDocument(doc)" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <common-buttons v-if="!selectedDocument && !isLoading && documents.length > 0 && documents.length < totalCount">
            <common-button
                :label="$t('pages.physicians.show.buttons.view_more')"
                type="primary"
                @click="viewMore"
            ></common-button>
        </common-buttons>

        <!-- Document Detail View -->
        <div v-if="selectedDocument" class="detail-view">
            <div v-if="!isEditBtnClicked">
                <div class="detail-header">
                    <h3>{{ selectedDocument?.fileName }}</h3>
                    <div class="edit-download">
                        <button v-if="showEditBtn" class="btn" @click="allowEdit">
                            <common-icon name="edit" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.detail_form.button.edit') }}
                        </button>
                        <button class="btn" @click="downloadDocument">
                            <common-icon name="download" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.detail_form.button.title') }}
                        </button>
                    </div>
                </div>

                <div class="detail-body">
                    <div class="preview-box">
                        <template v-if="isPreviewLoading">
                            <div class="loading-spinner">
                                <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                                <loading-spin></loading-spin>
                            </div>
                        </template>
                        <iframe v-else-if="isPdf && fileBlobUrl" :src="fileBlobUrl" class="pdf-viewer" frameborder="0"></iframe>
                        <img v-else-if="isImage && fileBlobUrl" :src="fileBlobUrl" class="image-viewer" />
                        <div v-else class="preview-placeholder">
                            <common-icon name="file" size="extra-large"></common-icon>
                            <p>{{ $t('modals.patient_profile.profile.detail_form.card_heading') }}</p>
                            <span>{{ selectedDocument.fileName }} • {{ formatFileSize(selectedDocument.size) }}</span>
                            <a :href="fileBlobUrl" target="_blank">
                                {{ $t('common.images.download') }}
                            </a>
                        </div>
                    </div>

                    <div class="doc-details">
                        <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.title') }}</h4>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.type') }}</span>
                            <span class="doc-tag">{{ selectedDocument?.recordType?.name }}</span>
                        </p>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.date') }}</span>
                            {{ dateFormate(selectedDocument.date) }}
                        </p>
                        <p>
                            <span class="data">
                                {{ $t('modals.patient_profile.profile.detail_form.document_detail.provider') }}
                            </span>
                            {{ selectedDocument?.source?.name }}
                        </p>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.size') }}</span>
                            {{ formatFileSize(selectedDocument.size) }}
                        </p>
                        <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.description') }}</h4>
                        <p>{{ selectedDocument?.note }}</p>

                        <button class="back-btn" @click="selectedDocument = null">
                            {{ $t('modals.patient_profile.profile.detail_form.document_detail.button.title') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-else>
                <form-wrapper>
                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.fileName.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.fileName.placeholder')"
                                :isRequired="true"
                                :formData="data"
                                valuePath="fileName"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.document.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.document.placeholder')"
                                :isRequired="true"
                                type="date"
                                :formData="data"
                                valuePath="date"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>
                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.label') }}
                            </label>
                            <select-input
                                class="schedule-apply__input"
                                :placeholder="$t('forms.patient_profile.fields.profile.placeholder')"
                                :hasAutofocus="true"
                                :options="documentOptions"
                                :formData="data"
                                :value="data.recordType.id"
                                @change="changeCurrentDocumentId($event)"
                            ></select-input>
                        </form-cell>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.provider.label') }}
                            </label>
                            <div :class="searchAutocompleteClass">
                                <div :class="iconClass">
                                    <common-icon :name="iconName" size="large"></common-icon>
                                </div>

                                <input
                                    v-model="searchTerm"
                                    type="text"
                                    class="search-autocomplete__input"
                                    :placeholder="$t('forms.patient_profile.fields.profile.provider.placeholder')"
                                    :disabled="!!selectedUser"
                                    @keyup="inputChange"
                                    @focus="inputFocus"
                                    @blur="inputBlur"
                                />
                                <div v-if="!!items.length" class="search-autocomplete__suggestions">
                                    <div v-for="(item, index) in items" :key="index">
                                        <single-item
                                            :disableNetworkPlan="disableNetworkPlan"
                                            :item="item"
                                            @make-referral="userSelected($event)"
                                        ></single-item>
                                    </div>
                                </div>
                                <common-user-label
                                    v-if="selectedUser"
                                    class="search-autocomplete__user-label"
                                    :user="selectedUser"
                                    :isDisabled="isDisabled"
                                    @close="removeSelectedUser()"
                                ></common-user-label>
                            </div>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.comment.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.comment.placeholder')"
                                :isRequired="true"
                                :formData="data"
                                valuePath="note"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>
                </form-wrapper>
                <common-buttons style="margin-top: 20px">
                    <common-button
                        :isSubmittable="false"
                        :label="$t('pages.patient.details.buttons.secondary')"
                        type="secondary"
                        @click="disAllowEdit"
                    ></common-button>
                    <common-button
                        :isProcessing="isSending"
                        :label="$t('pages.patient.details.buttons.primary')"
                        type="primary"
                        @click="updateDocument"
                    ></common-button>
                    <common-button
                        :isSubmittable="false"
                        :label="$t('modals.patient_profile.profile.detail_form.document_detail.button.title')"
                        type="secondary"
                        @click="backToDocuments"
                    ></common-button>
                </common-buttons>
            </div>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>

        <common-modal
            v-if="showDeleteModal"
            :heading="$t('modals.patient_profile.profile.detail_form.delete_modal.heading')"
            width="small"
            @close="cancelDelete"
        >
            <div class="delete-modal-body">
                <p>{{ $t('modals.patient_profile.profile.detail_form.delete_modal.message') }}</p>
                <common-buttons align="center">
                    <common-button
                        :isSubmittable="false"
                        :label="$t('modals.patient_profile.profile.detail_form.delete_modal.cancel')"
                        type="secondary"
                        @click="cancelDelete"
                    ></common-button>
                    <common-button
                        :isProcessing="isDeleting"
                        :label="$t('modals.patient_profile.profile.detail_form.delete_modal.confirm')"
                        type="primary"
                        @click="confirmDelete"
                    ></common-button>
                </common-buttons>
            </div>
        </common-modal>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import PatientAPI from 'api/PatientAPI'
    import FileMixin from 'mixins/FileMixin'
    import { Circle9 } from 'vue-loading-spinner'
    import AuthService from 'services/AuthService'
    import config from '../../../config'
    import debounce from 'lodash.debounce'
    import SingleItem from 'components/page-elements/search-autocomplete/SearchProviderAutocompleteSingleItem'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import { dateFormate } from '~/helpers'

    export default {
        mixins: [FormMixin, HandleRequestMixin, FileMixin],
        components: {
            'loading-spin': Circle9,
            SingleItem,
        },
        props: {
            documentOptions: { type: Array, default: () => [] },
            iconPosition: { type: String, default: 'left' },
            disableNetworkPlan: { type: Boolean, default: false },
            icon: { type: String, default: 'member' },
            isDisabled: { type: Boolean, default: false },
        },
        data() {
            return {
                patientId: null,
                selectedDocument: null,
                recordTypeId: 0,
                sourceId: 0,
                sourceOptions: [],
                documents: [],
                totalCount: 0,
                searchText: '',
                offset: 0,
                limit: 10,
                isLoading: false,
                images: [],
                selectedDocuments: [],
                fileBlobUrl: null,
                fileExtension: null,
                isPreviewLoading: false,
                isEditBtnClicked: false,
                searchTerm: '',
                selectedUser: null,
                isFocused: false,
                items: [],
                data: {
                    fileName: '',
                    date: '',
                    recordType: null,
                    source: null,
                    note: '',
                },
                notificationMessage: '',
                isSending: false,
                showDeleteModal: false,
                documentToDelete: null,
                isDeleting: false,
            }
        },

        computed: {
            searchAutocompleteClass() {
                return {
                    [`search-autocomplete__search-input--${this.iconPosition}`]: true,
                    'search-autocomplete__search-input': true,
                    'form-input-text': true,
                }
            },
            iconName() {
                if (this.iconBackground === 'primary' && this.isFocused) return `${this.icon}-white`
                return `${this.icon}-gray`
            },
            iconClass() {
                return {
                    'search-autocomplete__icon': true,
                    [`search-autocomplete__icon--${this.iconPosition}`]: true,
                    [`search-autocomplete__icon--color-${this.iconBackground}`]: this.isFocused,
                }
            },
            isAllSelected() {
                return this.documents.length > 0 && this.documents.every((doc) => doc.selected)
            },
            isPdf() {
                return this.fileExtension === 'pdf'
            },
            isImage() {
                return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(this.fileExtension)
            },
            typeOptions() {
                return [{ label: this.$t('modals.patient_profile.profile.type.text'), value: 0 }, ...this.documentOptions]
            },
            hasSelectedDocuments() {
                return this.selectedDocuments.length > 0
            },
            showEditBtn() {
                return this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.$auth.getPractice().id === this.selectedDocument.fromPracticeId
                    : this.$auth.getActiveMember().id === this.selectedDocument.fromProviderId
            },
            activeMemberId() {
                return this.$auth.getActiveMember().id
            },
        },
        methods: {
            dateFormate,
            extractProviderName(sourceName) {
                if (!sourceName) return ''
                return sourceName.split(',')[0].trim()
            },
            changeCurrentDocumentId(val) {
                this.recordTypeId = val
                const matched = this.documentOptions.find((opt) => Number(opt.value) === Number(val))
                if (matched) {
                    this.data.recordType = {
                        id: matched.value,
                        name: matched.label,
                    }
                }
            },

            inputChange: debounce(function () {
                this.emitEvent({ name: 'input-change' })
                this.fetchProviders({
                    searchTerm: this.searchTerm,
                    includeTyped: true,
                    autoSelect: false,
                })
            }, config.DEBOUNCE.DEFAULT),

            userSelected(event) {
                if (event?.type === 'typed') return
                this.searchTerm = ''
                this.items = []
                this.selectedUser = event
                this.emitEvent({ name: 'user-select', payload: event })
            },

            removeSelectedUser() {
                this.selectedUser = null
                this.emitEvent({ name: 'user-select', payload: null })
            },

            inputFocus() {
                this.isFocused = true
            },

            inputBlur() {
                this.isFocused = false
            },
            downloadSelected() {
                this.selectedDocuments.forEach((doc) => {
                    this.downloadFile(doc.url, doc.title)
                })
            },

            printSelected() {
                this.selectedDocuments.forEach((doc) => {
                    const routeData = this.$router.resolve({
                        name: 'image.preview',
                        query: { url: doc.url },
                    })
                    window.open(routeData.href, '_blank')
                })
            },
            toggleSelectAll(event) {
                const checked = event.target.checked

                this.documents.forEach((doc) => {
                    doc.selected = checked
                })

                this.selectedDocuments = checked ? [...this.documents] : []
            },
            toggleDocument(doc) {
                doc.selected = !doc.selected
                if (doc.selected) {
                    if (!this.selectedDocuments.find((d) => d.id === doc.id)) {
                        this.selectedDocuments.push(doc)
                    }
                } else {
                    this.selectedDocuments = this.selectedDocuments.filter((d) => d.id !== doc.id)
                }
            },
            changeTypeId(val) {
                this.recordTypeId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            changeSourceId(val) {
                this.sourceId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            getSources() {
                PatientAPI.getSources(this.patientId).then((res) => {
                    this.sourceOptions = [
                        { label: this.$t('modals.patient_profile.profile.source.text'), value: 0 },
                        ...res.map((item) => ({
                            label: item.name,
                            value: item.id,
                        })),
                    ]
                })
            },

            getDocuments(append = false) {
                this.isLoading = true
                return PatientAPI.getDocuments(this.patientId, {
                    limit: this.limit,
                    offset: this.offset,
                    recordTypeId: this.recordTypeId || undefined,
                    sourceId: this.sourceId || undefined,
                    search: this.searchText || undefined,
                })
                    .then((res) => {
                        this.totalCount = Number(res._headers['x-total-count'])
                        const mapped = res.map((item) => {
                            return {
                                id: item.id,
                                title: item.fileName,
                                date: dateFormate(item.date),
                                provider: item?.source?.name || 'Unknown',
                                size: this.formatFileSize(item.size),
                                type: item?.recordType?.name || 'Unknown',
                                icon: this.getDocIcon(item?.recordType?.id),
                                description: item.note || 'No description available',
                                url: item.url,
                                fromProviderId: item.fromProviderId,
                                selected: false,
                            }
                        })
                        if (append) {
                            this.documents = [...this.documents, ...mapped]
                        } else {
                            this.documents = mapped
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${kb} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            getDocIcon(type) {
                switch (type) {
                    case 1:
                        return '🧪'
                    case 2:
                        return '📷'
                    case 3:
                        return '👩‍⚕️'
                    case 4:
                        return '💊'
                    default:
                        return '📄'
                }
            },

            onSearch() {
                clearTimeout(this._searchDelay)
                this._searchDelay = setTimeout(() => {
                    this.offset = 0
                    this.documents = []
                    this.getDocuments()
                }, 400)
            },

            viewMore() {
                this.offset = this.offset + this.limit
                this.getDocuments(true)
            },

            getExtension(url) {
                const source = url
                if (!source) return ''
                return source.split('.').pop().toLowerCase()
            },

            viewDocument(documentId) {
                this.fileBlobUrl = null
                this.fileExtension = null
                this.isPreviewLoading = true

                PatientAPI.getSingleDocument(this.patientId, documentId).then((res) => {
                    this.selectedDocument = res
                    this.fetchFile(res)
                })
            },
            fetchFile(document) {
                if (this.fileBlobUrl) {
                    URL.revokeObjectURL(this.fileBlobUrl)
                }

                this.fileBlobUrl = null
                this.fileExtension = this.getExtension(document.url)
                this.isPreviewLoading = true

                fetch(document.url, {
                    headers: {
                        Authorization: `Bearer ${AuthService.getToken()}`,
                    },
                })
                    .then((res) => res.blob())
                    .then((blob) => {
                        this.fileBlobUrl = URL.createObjectURL(blob)
                    })
                    .catch(() => {
                        this.fileBlobUrl = null
                    })
                    .finally(() => {
                        this.isPreviewLoading = false
                    })
            },
            downloadDocument() {
                this.downloadFile(this.selectedDocument.url, this.selectedDocument.fileName)
            },
            allowEdit() {
                const doc = this.selectedDocument
                this.data = {
                    fileName: doc.fileName,
                    date: doc.date?.split('T')[0],
                    recordType: doc.recordType,
                    source: doc.source,
                    note: doc.note,
                }
                const providerName = this.extractProviderName(doc?.source?.name)
                this.searchTerm = providerName
                this.fetchProviders({
                    searchTerm: providerName,
                    autoSelect: true,
                    includeTyped: false,
                })
                this.isEditBtnClicked = true
            },
            disAllowEdit() {
                this.isEditBtnClicked = false
                this.data = {
                    fileName: '',
                    date: '',
                    recordType: null,
                    source: null,
                    note: '',
                }
            },
            backToDocuments() {
                ;(this.selectedDocument = null), this.disAllowEdit()
            },
            fetchProviders({ searchTerm, autoSelect = false, includeTyped = false }) {
                if (!searchTerm || searchTerm.length < 2) {
                    this.items = []
                    return
                }

                return PatientAPI.getProviderFromNetwork(searchTerm, 5, 0).then((results) => {
                    if (!results?.length) {
                        this.items = []
                        this.emitEvent?.({
                            name: 'no-results',
                            payload: { searchTerm },
                        })
                        return
                    }

                    if (autoSelect) {
                        const matched = results.find((item) => {
                            return (
                                +item.id === +this.selectedDocument.fromProviderId ||
                                item.name.toLowerCase() === searchTerm.toLowerCase()
                            )
                        })

                        if (matched) {
                            this.selectedUser = matched
                            this.items = []
                            this.searchTerm = ''
                            return
                        }
                    }

                    let finalItems = [...results]

                    if (includeTyped) {
                        finalItems.unshift({
                            name: searchTerm,
                            type: 'typed',
                        })
                    }

                    this.items = finalItems
                })
            },
            updateDocument() {
                this.isSending = true
                this.notificationMessage = ''
                const doc = this.selectedDocument
                const activeMember = this.$auth.getActiveMember()

                let sourcePracticeId = doc.sourcePracticeId || null
                let sourceUserId = doc.sourceUserId || null
                let sourceName = doc.source?.name || ''

                if (this.selectedUser) {
                    if (this.selectedUser.type === 'user') {
                        sourcePracticeId = this.selectedUser.practice?.id
                        sourceUserId = this.selectedUser.id
                    }

                    if (this.selectedUser.type === 'practice') {
                        sourcePracticeId = this.selectedUser.id
                        sourceUserId = null
                    }

                    sourceName = this.selectedUser.designation
                        ? `${this.selectedUser.name}, ${this.selectedUser.designation}`
                        : this.selectedUser.name
                }

                const payload = {
                    documentId: doc.id,
                    patientId: Number(this.patientId),
                    fileName: this.data.fileName || doc.fileName,
                    recordTypeId: this.recordTypeId || doc.recordType?.id,
                    date: this.data.date || doc.date?.split('T')[0],
                    sourceName,
                    sourcePracticeId,
                    sourceUserId,
                    note: this.data.note ?? doc.note,
                    activeMemberId: activeMember.id,
                }

                try {
                    PatientAPI.updateDocuments(payload).then(() => {
                        this.notificationMessage = this.$t('pages.patient.profile.notifications.update_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        this.disAllowEdit()
                        this.selectedDocument = null
                        this.selectedUser = null
                        this.searchTerm = ''
                        this.offset = 0
                        this.documents = []
                        this.getDocuments()
                    })
                } finally {
                    this.isSending = false
                }
            },
            isDocumentOwner(doc) {
                return doc.fromProviderId === this.activeMemberId
            },

            deleteDocument(doc) {
                this.documentToDelete = doc
                this.showDeleteModal = true
            },

            cancelDelete() {
                this.showDeleteModal = false
                this.documentToDelete = null
            },

            confirmDelete() {
                if (!this.documentToDelete) return
                this.isDeleting = true

                PatientAPI.deleteDocument(this.documentToDelete.id, this.activeMemberId)
                    .then(() => {
                        const id = this.documentToDelete.id
                        this.documents = this.documents.filter((d) => d.id !== id)
                        this.selectedDocuments = this.selectedDocuments.filter((d) => d.id !== id)
                        this.totalCount = Math.max(0, this.totalCount - 1)
                        this.notificationMessage = this.$t('modals.patient_profile.profile.detail_form.delete_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                        this.cancelDelete()
                    })
                    .catch(() => {
                        this.notificationMessage = this.$t('modals.patient_profile.profile.detail_form.delete_error')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .finally(() => {
                        this.isDeleting = false
                    })
            },
        },
        mounted() {
            this.patientId = this.$route.params.id
            this.getDocuments().then(() => {
                this.$emit('documents-loaded')
                this.getSources()
            })
        },
    }
</script>

<style scoped>
    .loading-spinner {
        text-align: center;
        font-size: 16px;

        .spinner {
            margin: 0 auto;
            zoom: 150%;
        }
    }
    .document-search-list {
        display: flex;
        border-bottom: 1px solid #ddd;
        padding-bottom: 15px;
        margin-bottom: 20px;
    }
    .filter-bar {
        display: flex;
        gap: 10px;
        position: relative;
        width: 100%;
    }
    .search-input {
        flex: 1;
        padding: 8px 29px;
        border-radius: 6px;
        border: 1px solid #f5f5f5;
        font-size: 12px;
        background-color: #f5f5f5;
        height: 51px;
    }
    .schedule-apply {
        width: 200px;
        margin-left: 20px;
        padding: 0;
    }
    .download-print-btns {
        margin-bottom: 10px;
    }
    .input-checkbox {
        cursor: pointer;
        width: 14px;
        height: 14px;
        margin-left: 18px;
    }
    .document-list {
        background-color: #fff;
        padding: 16px;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        margin-bottom: 20px;
    }
    .card-title {
        font-size: 18px;
        font-weight: 500;
        margin: 10px 0;
        color: oklch(0.145 0 0);
    }
    .document-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: white;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 10px;
        border: 1px solid rgba(0, 0, 0, 0.1);
    }
    .document-card:hover {
        background-color: oklch(0.985 0.002 247.839);
    }
    .doc-icon {
        font-size: 24px;
    }
    .doc-info {
        flex: 1;
        margin-left: 10px;
    }
    .doc-title {
        font-weight: 500;
        font-size: 14px;
        color: oklch(0.145 0 0);
    }
    .doc-meta {
        font-size: 12px;
        color: gray;
    }
    .doc-tag {
        background: oklch(0.95 0.0058 264.53);
        padding: 4px 8px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        color: #030213;
        margin-right: 18px;
    }
    .doc-actions button {
        margin-left: 20px;
        border: none;
        background: none;
        cursor: pointer;
    }
    .doc-actions {
        display: flex;
        align-items: center;
        gap: 14px;
    }
    .doc-checkbox-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .doc-delete-icon {
        cursor: pointer;
        color: #e53e3e;
        &:hover {
            opacity: 0.75;
        }
    }
    .delete-modal-body {
        padding: 8px 0 4px;
        p {
            margin-bottom: 24px;
            color: #4a5568;
            font-size: 14px;
            line-height: 1.5;
        }
    }
    .detail-view {
        background: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    .detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .detail-header h3 {
        font-weight: 500;
        font-size: 14px;
        color: oklch(0.145 0 0);
    }
    .btn {
        padding: 4px 12px;
        color: oklch(25.723% 0.00149 16.173);
        border-radius: 8px;
        font-weight: 500;
        font-size: 13px;
        border: 1px solid #d1d5db;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .detail-body {
        display: flex;
        gap: 20px;
        margin-top: 20px;
    }
    .preview-box {
        flex: 2;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px;
        background: #f8f9fa;
        text-align: center;
    }
    .preview-placeholder p {
        font-size: 16px;
        font-weight: 400;
        margin: 8px 10px;
    }
    .preview-placeholder span {
        font-size: 12px;
        font-weight: 400;
    }
    .doc-details {
        flex: 1;
    }
    .doc-details h4 {
        color: oklch(0.145 0 0);
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 8px;
    }
    .doc-details p {
        color: oklch(0.145 0 0);
        font-size: 12px;
        margin-bottom: 9px;
    }
    .data {
        margin-right: 8px;
        color: oklch(0.446 0.03 256.802);
    }
    .status-tag {
        background: black;
        color: white;
        padding: 2px 6px;
        border-radius: 6px;
        font-size: 12px;
    }
    .back-btn {
        padding: 8px 14px;
        color: oklch(0.546 0.245 262.881);
        border-radius: 10px;
        font-weight: 500;
        font-size: 14px;
        background: oklch(0.932 0.032 255.585);
        cursor: pointer;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: all 0.2s ease;
    }

    .back-btn:hover {
        background-color: oklch(0.546 0.245 262.881);
        color: white;
    }

    .back-btn:active {
        transform: scale(0.98);
    }

    .back-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
    }
    .tab-filter {
        display: flex;
    }
    .filter-bar i {
        position: absolute;
        top: 15px;
        left: 7px;
    }
    @media screen and (max-width: 600px) {
        .document-search-list,
        .document-card,
        .detail-body {
            display: block !important;
        }
        .schedule-apply {
            margin-left: 3px !important;
            margin-top: 7px !important;
        }
    }
    .pdf-viewer {
        width: 100%;
        height: 650px;
        border: 1px solid #ddd;
        border-radius: 6px;
    }

    .image-viewer {
        max-width: 100%;
        max-height: 650px;
        object-fit: contain;
        border-radius: 6px;
    }
    .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .select-all {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        cursor: pointer;
        margin-right: 12px;
    }

    .select-all input {
        cursor: pointer;
        height: 14px;
        width: 14px;
    }

    .edit-download {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 2px;
    }
</style>
